/*
  File: cart-drawer-v4.js
  Version: 1.0
  Date: 2026-04-22
  Purpose: Interaction layer for the Sidecart v4 redesign. All handlers use
  event delegation on <cart-drawer> so they survive section swaps (the drawer's
  inner HTML is replaced wholesale after add-to-cart / qty change).

  Responsibilities:
    - Header sheet toggles (note, share), mutually exclusive, Esc closes.
    - Cart note autosave (debounced POST to /cart/update.js), char counter,
      unsaved-content indicator dot on the header note icon.
    - Share URL construction (Shopify cart permalink) + clipboard copy.
    - Quantity editor collapse/expand per line item.
    - Save button handler (feature-flagged via theme setting); v1 behavior =
      remove from cart. Real save-for-later list is a separate project.
*/

(function () {
  const NOTE_DEBOUNCE_MS = 400;
  const COPIED_FEEDBACK_MS = 1400;

  function init() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (drawer.dataset.v4Initialized === '1') return;
    drawer.dataset.v4Initialized = '1';

    wireDelegatedHandlers(drawer);
    refreshDynamic(drawer);

    // Deterministic hook: CartDrawer dispatches 'cart-drawer:hydrate'
    // right after swapping #CartDrawer innerHTML, with a
    // `detail.promises` array we can push onto. The View Transition
    // waits on those promises so the crossfade captures the fully
    // populated DOM — suggestions never pop in after the fact.
    drawer.addEventListener('cart-drawer:hydrate', (e) => {
      const list = e && e.detail && e.detail.promises;
      const p = refreshDynamic(drawer);
      if (list && p && typeof p.then === 'function') list.push(p);
    });
    // Post-hydration listeners (for anything that just wants to know
    // the drawer is done rendering).
    drawer.addEventListener('cart-drawer:rendered', () => {});
    document.addEventListener('cart-drawer:refresh', () => refreshDynamic(drawer));
  }

  function wireDelegatedHandlers(drawer) {
    drawer.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-drawer-toggle]');
      if (toggleBtn) {
        handleSheetToggle(drawer, toggleBtn);
        return;
      }
      const shareCopy = e.target.closest('[data-share-copy]');
      if (shareCopy) {
        handleShareCopy(drawer, shareCopy);
        return;
      }
      const saveBtn = e.target.closest('[data-save-line-key]');
      if (saveBtn) {
        handleSaveForLater(drawer, saveBtn);
        return;
      }
      const addSuggestion = e.target.closest('[data-suggestion-add]');
      if (addSuggestion) {
        handleSuggestionAdd(drawer, addSuggestion);
        return;
      }
      const plusBtn = e.target.closest('quantity-input button[name="plus"]');
      if (plusBtn) {
        // QuantityInput silently no-ops when value >= max (stepUp()
        // refuses to increment past max and no change event fires).
        // That leaves the shopper with a dead button and no idea why
        // — surface "Only N left in stock" inline so the cap is
        // visible. Server-side stock errors are reworded to the same
        // string in cart.js so the two paths feel consistent.
        handleQtyMaxFeedback(drawer, plusBtn);
        return;
      }
    });

    drawer.addEventListener(
      'input',
      debounce((e) => {
        if (e.target && e.target.id === 'CartDrawer-Note') {
          handleNoteInput(drawer, e.target);
        }
      }, NOTE_DEBOUNCE_MS)
    );

    drawer.addEventListener('input', (e) => {
      if (e.target && e.target.id === 'CartDrawer-Note') {
        updateNoteCounter(drawer, e.target);
      }
    });

    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openSheet = drawer.querySelector('.cd-sheet.is-open');
        if (openSheet) {
          e.stopPropagation();
          closeAllSheets(drawer);
        }
      }
    });
  }

  /* ───────── Sheet toggles ───────── */

  function handleSheetToggle(drawer, btn) {
    const key = btn.getAttribute('data-drawer-toggle');
    const sheetId = btn.getAttribute('aria-controls');
    const sheet = sheetId ? drawer.querySelector('#' + sheetId) : null;
    if (!sheet) return;

    const isOpen = sheet.classList.contains('is-open');
    closeAllSheets(drawer);
    if (!isOpen) {
      sheet.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      if (key === 'share') populateShareUrl(drawer);
      const focusEl = sheet.querySelector('textarea, input, button');
      if (focusEl) focusEl.focus();
    }
  }

  function closeAllSheets(drawer) {
    drawer.querySelectorAll('.cd-sheet').forEach((s) => s.classList.remove('is-open'));
    drawer.querySelectorAll('[data-drawer-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    updateNoteIndicator(drawer);
  }

  /* ───────── Note autosave ───────── */

  function handleNoteInput(drawer, textarea) {
    const note = textarea.value || '';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    }).catch((err) => console.error('[cart-drawer-v4] note save failed', err));
  }

  function updateNoteCounter(drawer, textarea) {
    const counter = drawer.querySelector('[data-note-count]');
    if (counter) counter.textContent = String((textarea.value || '').length);
  }

  function updateNoteIndicator(drawer) {
    const textarea = drawer.querySelector('#CartDrawer-Note');
    const indicator = drawer.querySelector('[data-note-indicator]');
    const sheet = drawer.querySelector('#CartDrawer-NoteSheet');
    if (!indicator || !textarea || !sheet) return;
    const hasContent = (textarea.value || '').trim().length > 0;
    const sheetClosed = !sheet.classList.contains('is-open');
    indicator.hidden = !(hasContent && sheetClosed);
  }

  /* ───────── Share URL ───────── */

  function buildCartPermalink(drawer) {
    const items = drawer.querySelectorAll('.cd-item');
    const parts = [];
    items.forEach((row) => {
      const variantId = row.getAttribute('data-variant-id');
      const qtyInput = row.querySelector('input[name="updates[]"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 0;
      if (variantId && qty > 0) parts.push(variantId + ':' + qty);
    });
    if (parts.length === 0) return window.location.origin + '/cart';
    return window.location.origin + '/cart/' + parts.join(',');
  }

  function populateShareUrl(drawer) {
    const el = drawer.querySelector('[data-share-url]');
    if (!el) return;
    const url = buildCartPermalink(drawer);
    el.textContent = url;
    el.dataset.url = url;
  }

  function handleShareCopy(drawer, btn) {
    const urlEl = drawer.querySelector('[data-share-url]');
    const url = (urlEl && (urlEl.dataset.url || urlEl.textContent || '').trim()) || buildCartPermalink(drawer);
    const copyLabel = btn.querySelector('[data-copy-label]');

    const fallbackCopy = () => {
      const t = document.createElement('textarea');
      t.value = url;
      t.style.position = 'fixed';
      t.style.opacity = '0';
      document.body.appendChild(t);
      t.select();
      try {
        document.execCommand('copy');
      } catch (_) {}
      document.body.removeChild(t);
    };

    const flashCopied = () => {
      btn.setAttribute('data-copied', '');
      if (copyLabel) copyLabel.textContent = 'Copied';
      setTimeout(() => {
        btn.removeAttribute('data-copied');
        if (copyLabel) copyLabel.textContent = 'Copy';
      }, COPIED_FEEDBACK_MS);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(flashCopied, () => {
        fallbackCopy();
        flashCopied();
      });
    } else {
      fallbackCopy();
      flashCopied();
    }
  }

  /* ───────── Qty cap feedback ───────── */

  function handleQtyMaxFeedback(drawer, plusBtn) {
    const wrap = plusBtn.closest('quantity-input');
    if (!wrap) return;
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    const max = parseInt(input.getAttribute('max'), 10);
    const value = parseInt(input.value, 10);
    if (!Number.isFinite(max) || !Number.isFinite(value)) return;
    if (value < max) return; // increment will succeed; let the normal flow handle it
    const row = input.closest('.cart-item');
    if (!row) return;
    const errEl = row.querySelector('.cart-item__error-text');
    if (!errEl) return;
    // Persist until the next successful qty change for this line —
    // the section swap in cart.js _reconcile will replace the error
    // region's HTML and naturally clear the message. No auto-clear
    // timer; an in-stock cap is still relevant a few seconds later.
    errEl.textContent = `Only ${max} left in stock`;
  }

  /* ───────── Save-for-later (feature-flagged; v1 = remove) ───────── */

  function handleSaveForLater(drawer, btn) {
    // TODO: implement real saved-for-later list (customer-scoped or cookie-backed).
    // For now, treat Save as Remove so the button has a predictable effect.
    const lineKey = btn.getAttribute('data-save-line-key');
    if (!lineKey) return;
    const escaped = String(lineKey).replace(/(["\\])/g, '\\$1');
    const removeBtn = drawer.querySelector(`cart-remove-button[data-line-key="${escaped}"] button`);
    if (removeBtn) removeBtn.click();
  }

  /* ───────── Suggestions (Shopify complementary recommendations) ───────── */

  const SUGGESTIONS_LIMIT_DEFAULT = 3;
  const SUGGESTIONS_CACHE = new Map();

  // Active suggestions list — once we've shown a set of recommendations,
  // keep reusing them as items get added to cart. We only refetch when
  // every item we showed is now in the cart (the user exhausted the
  // list). Prevents the jarring shuffle where Shopify re-recommends
  // based on the newly-added item and swaps the list under the user.
  let activeSuggestionsProducts = null;

  function readSuggestionsLimit(container) {
    const raw = container && container.getAttribute('data-suggestions-limit');
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : SUGGESTIONS_LIMIT_DEFAULT;
  }

  // Remove only the cards this file appended, leaving the server-rendered
  // pinned card in place. Replacing list.innerHTML would destroy it, and it
  // would not come back until the next full drawer render.
  function clearFetchedSuggestions(list) {
    Array.prototype.slice.call(list.children).forEach((el) => {
      if (!el.hasAttribute('data-suggestion-pinned')) el.remove();
    });
  }

  function cartVariantIds(drawer) {
    const ids = new Set();
    drawer.querySelectorAll('.cd-item').forEach((row) => {
      const id = row.getAttribute('data-variant-id');
      if (id) ids.add(String(id));
    });
    return ids;
  }

  // Extract the #cart-bootstrap-state JSON blob from a server-rendered
  // cart-drawer section HTML string and feed it to cartState. Lets us
  // update the store without an extra /cart.js fetch — the section
  // already contains a fresh authoritative snapshot.
  function updateCartStateFromSection(sectionHtml) {
    if (!window.cartState || !sectionHtml) return;
    try {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const blob = doc.getElementById('cart-bootstrap-state');
      if (!blob) return;
      const parsed = JSON.parse(blob.textContent || '{}');
      window.cartState.replaceFromServer(parsed);
    } catch (e) {
      console.error('[cart-drawer-v4] failed to extract cart state from section', e);
    }
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(cents) / 100);
    } catch (_) {
      return '$' + (Number(cents) / 100).toFixed(2);
    }
  }

  // Subscribe to the cart store and render the visible total whenever
  // it changes. The store is the source of truth — this is read-only
  // output. Replaces the previous cdAdjustCartTotal helper, which
  // mutated the DOM directly from multiple call sites and double-
  // counted on remove (CartRemoveButton + updateQuantity(0) both fired).
  if (window.cartState && !window.cartState.__cdSubtotalSubscribed) {
    window.cartState.__cdSubtotalSubscribed = true;
    const renderTotals = (state) => {
      const label = formatMoney(state.totalPriceCents);
      document.querySelectorAll('[data-cart-total]').forEach((el) => {
        // Keep the data-attr in sync as a convenience for non-JS
        // consumers; not used by this file as a source of math.
        el.setAttribute('data-cart-total', String(state.totalPriceCents));
        el.textContent = label;
      });
    };
    window.cartState.subscribe(renderTotals);
    // Initial paint — subscribe() doesn't fire immediately.
    renderTotals(window.cartState.getState());
  }

  function loadSuggestions(drawer) {
    const container = drawer.querySelector('[data-suggestions]');
    const list = drawer.querySelector('[data-suggestions-list]');
    if (!container || !list) return Promise.resolve();

    // The pinned add-on card is server-rendered by
    // snippets/cart-pinned-suggestion.liquid as the first child of the
    // list. It is content, not a fetch result: this function appends
    // around it and never owns it. Consequences that fall out of that —
    // it survives drawer swaps, skips the already-in-cart filter, and
    // costs no upsell slot — are the reason it lives in Liquid.
    const pinned = list.querySelector('[data-suggestion-pinned]');
    const pinnedId = pinned ? String(pinned.getAttribute('data-suggestion-id') || '') : '';

    const seed = container.getAttribute('data-suggestions-seed');
    if (!seed) {
      container.hidden = !pinned;
      return Promise.resolve();
    }

    const limit = readSuggestionsLimit(container);

    const render = (products) => {
      const inCart = cartVariantIds(drawer);
      const filtered = products
        .filter((p) => {
          if (!p.variants || p.variants.length === 0) return false;
          if (!p.variants.some((v) => v.available)) return false;
          // Never render the pinned product twice if the recommendation
          // API happens to return it as well.
          if (pinnedId && String(p.id) === pinnedId) return false;
          return !p.variants.some((v) => inCart.has(String(v.id)));
        })
        .slice(0, limit);

      clearFetchedSuggestions(list);

      if (filtered.length === 0) {
        container.hidden = !pinned;
        return;
      }

      const variant = container.getAttribute('data-suggestion-variant') || 'list';
      list.insertAdjacentHTML(
        'beforeend',
        filtered.map((p) => renderSuggestionRow(p, variant)).join('')
      );
      container.hidden = false;
    };

    // Reuse the active list if at least one item hasn't been added yet.
    // Only fetch a new batch once the user has added every item we showed.
    if (activeSuggestionsProducts && activeSuggestionsProducts.length) {
      const inCart = cartVariantIds(drawer);
      const anyStillAvailable = activeSuggestionsProducts.some((p) =>
        p.variants && p.variants.some((v) => v.available && !inCart.has(String(v.id)))
      );
      if (anyStillAvailable) {
        render(activeSuggestionsProducts);
        return Promise.resolve();
      }
      // Exhausted — fall through to a fresh fetch.
      activeSuggestionsProducts = null;
    }

    if (SUGGESTIONS_CACHE.has(seed)) {
      const cached = SUGGESTIONS_CACHE.get(seed);
      activeSuggestionsProducts = cached;
      render(cached);
      return Promise.resolve();
    }

    // intent=complementary: only the pairings configured per product in
    // Search & Discovery. Same source as the PDP's "You may also like"
    // (sections/related-products.liquid). A seed with no complementary
    // products returns none, and the block stays hidden (see render()).
    // Over-fetch the API max (10) so filtering out sold-out and
    // already-in-cart items still leaves enough to fill the configured
    // slot count.
    const url =
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(seed) +
      '&limit=10&intent=complementary';

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((data) => {
        const products = (data && data.products) || [];
        SUGGESTIONS_CACHE.set(seed, products);
        activeSuggestionsProducts = products;
        render(products);
      })
      .catch((err) => {
        console.error('[cart-drawer-v4] recommendations fetch failed', err);
        container.hidden = !pinned;
      });
  }

  function suggestionData(product) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const variantId = variant && variant.id;
    const priceCents =
      variant && typeof variant.price === 'number'
        ? variant.price
        : typeof product.price === 'number'
        ? product.price
        : 0;
    const image =
      (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      (product.images && product.images[0]) ||
      '';
    const title = escapeHtml(product.title || '');
    const priceLabel = formatMoney(priceCents);
    const productUrl = product.url
      ? product.url
      : (product.handle ? '/products/' + product.handle : '');

    // Stash the chosen variant's option name+value pairs so the optimistic
    // cart line built at click time can render the same options row the
    // real server-rendered line will have — matching height, no jump.
    const optionPairs = [];
    if (product.options && variant && Array.isArray(variant.options)) {
      for (let i = 0; i < product.options.length; i++) {
        const def = product.options[i];
        const name = typeof def === 'string' ? def : (def && def.name) || '';
        const value = variant.options[i];
        if (name && value && value !== 'Default Title') {
          optionPairs.push({ name: name, value: value });
        }
      }
    }
    return {
      productId: product.id,
      variantId: variantId,
      title: title,
      priceCents: priceCents,
      priceLabel: priceLabel,
      image: image,
      productUrl: productUrl,
      optionPairs: optionPairs,
    };
  }

  function renderSuggestionRow(product, variant) {
    return variant === 'cards'
      ? renderSuggestionCard(suggestionData(product))
      : renderSuggestionList(suggestionData(product));
  }

  // List variant — original compact row markup. Used when admin selects
  // "List" in cart settings.
  function renderSuggestionList(d) {
    const hrefAttr = d.productUrl ? ' href="' + escapeAttr(d.productUrl) + '"' : '';
    const imageInner = d.image
      ? '<img class="cd-suggestion__image" src="' + escapeAttr(d.image) +
        '" alt="" loading="lazy" width="42" height="42">'
      : '<span class="cd-suggestion__image" aria-hidden="true"></span>';
    const imageAttr = d.productUrl
      ? '<a class="cd-suggestion__media-link"' + hrefAttr +
        ' tabindex="-1" aria-hidden="true">' + imageInner + '</a>'
      : imageInner;
    const optionsAttr = d.optionPairs.length
      ? ' data-options="' + escapeAttr(JSON.stringify(d.optionPairs)) + '"'
      : '';
    let optionsRowHTML = '';
    if (d.optionPairs.length) {
      optionsRowHTML = '<div class="cd-suggestion__options">' + d.optionPairs.map(function (opt) {
        const key = (opt.name || '').toLowerCase();
        const safeValue = escapeHtml(opt.value);
        if (key === 'size') return '<span class="cd-item__pill">Size ' + safeValue + '</span>';
        if (key === 'color' || key === 'colour') {
          return '<span class="cd-item__color"><span class="cd-item__color-name">' + safeValue + '</span></span>';
        }
        return '<span class="cd-item__option">' + escapeHtml(opt.name) + ': ' + safeValue + '</span>';
      }).join('') + '</div>';
    }
    const nameHTML = d.productUrl
      ? '<a class="cd-suggestion__name"' + hrefAttr + '>' + d.title + '</a>'
      : '<div class="cd-suggestion__name">' + d.title + '</div>';
    return (
      '<div class="cd-suggestion" data-suggestion-id="' +
      escapeAttr(d.productId) + '" data-suggestion-price="' +
      escapeAttr(d.priceCents) + '"' + optionsAttr + '>' +
      imageAttr +
      '<div class="cd-suggestion__info">' +
      nameHTML + optionsRowHTML +
      '<div class="cd-suggestion__meta"><b>' + d.priceLabel + '</b></div>' +
      '</div>' +
      '<button type="button" class="cd-suggestion__add" data-suggestion-add="' +
      escapeAttr(d.variantId) +
      '" aria-label="Add ' + d.title + ' to cart">' +
      '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
      'Add' +
      '</button>' +
      '</div>'
    );
  }

  // Cards variant — square 140px tile in a horizontally scrolling row.
  // Keeps the same `cd-suggestion__image / __name / __meta b` hooks and
  // `data-options` attr that buildOptimisticCartLine reads, so the
  // optimistic-add path is variant-agnostic.
  function renderSuggestionCard(d) {
    const hrefAttr = d.productUrl ? ' href="' + escapeAttr(d.productUrl) + '"' : '';
    const optionsAttr = d.optionPairs.length
      ? ' data-options="' + escapeAttr(JSON.stringify(d.optionPairs)) + '"'
      : '';

    // Card image: full-width square well. Render an <img> if we have one,
    // otherwise an empty placeholder span (keeps the layout slot).
    const imageInner = d.image
      ? '<img class="cd-suggestion__image cd-suggestion__image--card" src="' +
        escapeAttr(d.image) + '" alt="" loading="lazy">'
      : '<span class="cd-suggestion__image cd-suggestion__image--card" aria-hidden="true"></span>';
    const imageWell = '<div class="cd-suggestion__well">' +
      (d.productUrl
        ? '<a class="cd-suggestion__media-link"' + hrefAttr +
          ' tabindex="-1" aria-hidden="true">' + imageInner + '</a>'
        : imageInner) +
      '</div>';

    // Meta line — "Size 10 · Ivory". Built by joining option values with
    // a middle dot. "Size" gets the literal "Size " prefix to match spec.
    let metaLine = '';
    if (d.optionPairs.length) {
      metaLine = d.optionPairs.map(function (opt) {
        const key = (opt.name || '').toLowerCase();
        const v = escapeHtml(opt.value);
        return key === 'size' ? 'Size ' + v : v;
      }).join(' · ');
    }

    const nameHTML = d.productUrl
      ? '<a class="cd-suggestion__name"' + hrefAttr + '>' + d.title + '</a>'
      : '<div class="cd-suggestion__name">' + d.title + '</div>';

    return (
      '<div class="cd-suggestion cd-suggestion--card" data-suggestion-id="' +
      escapeAttr(d.productId) + '" data-suggestion-price="' +
      escapeAttr(d.priceCents) + '"' + optionsAttr + '>' +
      imageWell +
      '<div class="cd-suggestion__text">' +
      nameHTML +
      (metaLine ? '<div class="cd-suggestion__meta-line">' + metaLine + '</div>' : '') +
      '</div>' +
      '<div class="cd-suggestion__footer">' +
      '<span class="cd-suggestion__price">' + d.priceLabel + '</span>' +
      '<button type="button" class="cd-suggestion__add cd-suggestion__add--card" data-suggestion-add="' +
      escapeAttr(d.variantId) +
      '" aria-label="Add ' + d.title + ' to cart">' +
      '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
      '<span>Add</span>' +
      '</button>' +
      // Hidden meta block: optimistic-line builder reads
      // .cd-suggestion__meta b for the price label. Keep the same hook
      // here (visually hidden) so cards stay drop-in compatible.
      '<span class="cd-suggestion__meta visually-hidden"><b>' + d.priceLabel + '</b></span>' +
      '</div>' +
      '</div>'
    );
  }

  // Clone an existing cart row as the structural template for the optimistic
  // line, then patch in the new product's data. This guarantees the optimistic
  // line has the same height, classes, and custom-element wrappers as the
  // server-rendered line that will replace it — without duplicating the row
  // markup in JS. Suggestions only render when the cart has items, so there
  // is always a row available to clone.
  function buildOptimisticCartLine(sourceRow, variantId) {
    if (!sourceRow) return null;
    const drawer = sourceRow.closest('cart-drawer');
    const template = drawer && drawer.querySelector('ul.cd-items > li.cd-item:not([data-optimistic])');
    if (!template) return null;

    const sImg = sourceRow.querySelector('.cd-suggestion__image');
    const sName = sourceRow.querySelector('.cd-suggestion__name');
    const sPrice = sourceRow.querySelector('.cd-suggestion__meta b');
    const title = sName ? sName.textContent.trim() : '';
    const price = sPrice ? sPrice.textContent.trim() : '';
    // Use the visible thumbnail's src — it's already loaded and decoded
    // in the browser since the carousel just rendered it, so the
    // optimistic line paints with an image instantly. The server-rendered
    // cart line that follows uses a different (variant-specific, sized)
    // URL, but renderContents awaits decode of those before snapshotting,
    // so the swap is invisible.
    const imgSrc = sImg && sImg.tagName === 'IMG' ? sImg.getAttribute('src') || '' : '';
    const imgAlt = sImg && sImg.tagName === 'IMG' ? sImg.getAttribute('alt') || '' : '';

    let options = [];
    try {
      const raw = sourceRow.getAttribute('data-options');
      if (raw) options = JSON.parse(raw) || [];
    } catch (_) {}

    const li = template.cloneNode(true);
    li.removeAttribute('id');
    li.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    li.classList.add('cd-item--optimistic');
    li.setAttribute('data-optimistic', 'true');
    li.setAttribute('aria-busy', 'true');
    li.setAttribute('data-variant-id', String(variantId));

    // Strip blocks that belong to the cloned product, not the new add.
    li.querySelectorAll('.cd-item__prop, .cd-item__plan, .cd-item__discounts, .cd-item__old-price').forEach((el) => el.remove());

    const imgEl = li.querySelector('.cd-item__image');
    if (imgEl) {
      if (imgSrc) {
        // Replace the cloned img element entirely. If we just changed
        // src on the existing element, the browser keeps displaying
        // the previous (template) image until the new src finishes
        // loading — so the optimistic line briefly shows the wrong
        // product's image. A fresh element starts blank.
        const fresh = document.createElement('img');
        fresh.className = imgEl.className;
        fresh.alt = imgAlt;
        fresh.loading = 'eager';
        fresh.decoding = 'sync';
        fresh.src = imgSrc;
        imgEl.replaceWith(fresh);
      } else {
        imgEl.remove();
      }
    }
    const mediaLink = li.querySelector('a.cd-item__media');
    if (mediaLink) mediaLink.removeAttribute('href');

    const nameEl = li.querySelector('.cd-item__name');
    if (nameEl) {
      nameEl.textContent = title;
      if (nameEl.tagName === 'A') nameEl.removeAttribute('href');
    }

    const priceEl = li.querySelector('.cd-item__final-price');
    if (priceEl && price) priceEl.textContent = price;

    // Options: rebuild from the suggestion row's stashed pairs.
    const optionsContainer = li.querySelector('.cd-item__options');
    if (options.length) {
      const html = options.map(function (opt) {
        const key = (opt.name || '').toLowerCase();
        const safeValue = escapeHtml(opt.value);
        if (key === 'size') return '<span class="cd-item__pill">Size ' + safeValue + '</span>';
        if (key === 'color' || key === 'colour') return '<span class="cd-item__color"><span class="cd-item__color-name">' + safeValue + '</span></span>';
        return '<span class="cd-item__option">' + escapeHtml(opt.name) + ': ' + safeValue + '</span>';
      }).join('');
      if (optionsContainer) {
        optionsContainer.innerHTML = html;
      } else {
        const top = li.querySelector('.cd-item__top');
        if (top) top.insertAdjacentHTML('afterend', '<div class="cd-item__options">' + html + '</div>');
      }
    } else if (optionsContainer) {
      optionsContainer.remove();
    }

    const qtyStrong = li.querySelector('.cd-qty__collapsed strong');
    if (qtyStrong) qtyStrong.textContent = '1';
    const qtyInput = li.querySelector('.cd-qty__input, .quantity__input');
    if (qtyInput) qtyInput.value = '1';

    li.querySelectorAll('button').forEach((b) => { b.disabled = true; b.tabIndex = -1; });

    const errText = li.querySelector('.cart-item__error-text');
    if (errText) errText.textContent = '';

    return li;
  }

  function handleSuggestionAdd(drawer, btn) {
    const variantId = btn.getAttribute('data-suggestion-add');
    if (!variantId) return;
    btn.disabled = true;

    // Optimistic add against the cart store. Store fires a change
    // event; the subtotal subscriber (above) re-renders [data-cart-total].
    // The DOM is never read as a source of truth — only written.
    const card = btn.closest('[data-suggestion-price]');
    const priceCents = card ? Number(card.getAttribute('data-suggestion-price')) : 0;
    if (priceCents && window.cartState) {
      window.cartState.addLine({
        variantId: Number(variantId),
        qty: 1,
        unitPriceCents: priceCents,
      });
    }

    const sourceRow = btn.closest('.cd-suggestion');
    const itemsList = drawer.querySelector('ul.cd-items');
    const scrollHost = drawer.querySelector('cart-drawer-items');
    const optimisticLine = buildOptimisticCartLine(sourceRow, variantId);

    // The shared-element morph only works visually when both endpoints are
    // in (or near) the viewport. If the cart is scrolled, the prepended line
    // lands above the visible area and the source flies past the top of the
    // drawer. In that case, skip the morph and just do a plain optimistic
    // insert — server HTML still replaces it as usual.
    const scrolled = !!scrollHost && scrollHost.scrollTop > 4;

    // Cards variant: skip the VT shared-element morph. A square 140px tile
    // doesn't share useful geometry with a wide cart line, so the morph
    // looks wrong. Instead, prepend the optimistic line immediately and
    // play a CSS collapse on the card before removing it (handled here by
    // toggling cd-suggestion--leaving and waiting for transitionend).
    const isCard = !!(sourceRow && sourceRow.classList.contains('cd-suggestion--card'));

    // The pinned add-on card is a standing offer, not a one-shot upsell —
    // the customer may add it repeatedly. It is never removed, never
    // animated out, and never restored by rollback (it never left).
    const isPinned = !!(sourceRow && sourceRow.hasAttribute('data-suggestion-pinned'));

    // Stash enough to roll back if the fetch fails.
    const rollback = {
      row: isPinned ? null : sourceRow,
      rowParent: sourceRow && !isPinned ? sourceRow.parentNode : null,
      rowNextSibling: sourceRow && !isPinned ? sourceRow.nextSibling : null,
      optimisticLine: optimisticLine,
    };

    // Promise that resolves once the source row has finished any leaving
    // animation. Server-response apply waits on this so the DOM swap
    // doesn't blow away the card mid-transition. Default to resolved.
    let leavePromise = Promise.resolve();

    if (isCard && sourceRow && optimisticLine && itemsList) {
      // Prepend the optimistic line right away so the cart area gets
      // immediate feedback, then animate the card out. transitionend
      // removes the source node (or a 350ms safety timeout, in case the
      // browser drops the event).
      // Tag the optimistic line with an enter class so CSS plays a
      // brief slide+fade-in. Scoped to the cards path so the list/VT
      // morph path is unaffected.
      //
      // Sequence:
      //   1. Prepend the line WITHOUT the enter class so it lays out
      //      at its natural height.
      //   2. Measure that height (scrollHeight).
      //   3. Add .cd-item--enter (collapses to max-height:0, opacity:0,
      //      translateY).
      //   4. Force a reflow so the initial state is committed.
      //   5. Add .cd-item--enter-active and set max-height inline to
      //      the measured value, so the slot grows to *exactly* the
      //      content's height — not a guessed cap. This keeps the
      //      slot-grow visually in lockstep with opacity/transform.
      // Mirror the remove animation in cart.js (inverted): set the
      // collapsed state inline, prepend, force a reflow, then flip to
      // the expanded state. Animates max-height + opacity + padding +
      // margin together with one easing so the row grows in as a
      // single coherent motion. Same vocabulary as remove on purpose.
      //
      // Measure the template row (an existing line, which the
      // optimistic line was cloned from) for the target height.
      const templateRow = drawer.querySelector(
        'ul.cd-items > li.cd-item:not([data-optimistic])'
      );
      const targetHeight = templateRow ? templateRow.offsetHeight : 0;

      // Collapsed initial state — inline, applied before the line
      // hits the DOM so there's no flash. Timings match cart.js
      // remove (inverse motion: same duration & easing).
      optimisticLine.style.maxHeight = '0';
      optimisticLine.style.opacity = '0';
      optimisticLine.style.paddingTop = '0';
      optimisticLine.style.paddingBottom = '0';
      optimisticLine.style.marginTop = '0';
      optimisticLine.style.marginBottom = '0';
      optimisticLine.style.overflow = 'hidden';
      optimisticLine.style.transition =
        'max-height 0.25s ease, opacity 0.2s ease, padding 0.25s ease, margin 0.25s ease';

      itemsList.prepend(optimisticLine);
      if (scrolled && scrollHost) scrollHost.scrollTop = 0;

      // Force reflow so the browser commits the collapsed state.
      void optimisticLine.offsetHeight;

      // Flip to the expanded state. Empty string on padding/margin
      // restores the stylesheet defaults; max-height is the measured
      // template height; opacity is 1.
      optimisticLine.style.maxHeight = (targetHeight || 320) + 'px';
      optimisticLine.style.opacity = '1';
      optimisticLine.style.paddingTop = '';
      optimisticLine.style.paddingBottom = '';
      optimisticLine.style.marginTop = '';
      optimisticLine.style.marginBottom = '';

      // After the entrance finishes, clear the inline overrides so
      // the real server-rendered line (which may differ by a pixel)
      // doesn't fight an inline cap. Filter to max-height + this
      // element so a descendant transition doesn't fire early.
      const clearCap = (e) => {
        if (e.target !== optimisticLine || e.propertyName !== 'max-height') return;
        optimisticLine.style.maxHeight = '';
        optimisticLine.style.overflow = '';
        optimisticLine.style.transition = '';
        optimisticLine.removeEventListener('transitionend', clearCap);
      };
      optimisticLine.addEventListener('transitionend', clearCap);
      leavePromise = isPinned ? Promise.resolve() : new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          if (sourceRow.parentNode) sourceRow.remove();
          resolve();
        };

        // Mirror cart.js remove + cart-line entrance pattern, just
        // horizontal: animate max-width + opacity + horizontal
        // padding + horizontal margin from natural to collapsed.
        // Same easing & duration as remove so the card disappearing
        // and the new line growing in feel like one motion.
        const startWidth = sourceRow.offsetWidth;
        sourceRow.style.maxWidth = startWidth + 'px';
        sourceRow.style.overflow = 'hidden';
        sourceRow.style.pointerEvents = 'none';
        sourceRow.style.transition =
          'max-width 0.25s ease, opacity 0.2s ease, padding 0.25s ease, margin 0.25s ease';
        // Force reflow so the browser commits the starting state
        // before we change values — otherwise both states land in
        // the same paint frame and the transition is skipped.
        void sourceRow.offsetHeight;
        sourceRow.style.maxWidth = '0';
        sourceRow.style.opacity = '0';
        sourceRow.style.paddingLeft = '0';
        sourceRow.style.paddingRight = '0';
        sourceRow.style.marginLeft = '0';
        // Cancel the flex row gap so siblings slide flush together
        // as this card collapses out.
        sourceRow.style.marginRight = '-10px';

        sourceRow.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, 350);
      });
    } else if (sourceRow && optimisticLine && itemsList && !scrolled && !isPinned && typeof document.startViewTransition === 'function') {
      // List variant w/ VT support: shared-element morph from row → line.
      sourceRow.classList.add('cd-morph-source');
      optimisticLine.classList.add('cd-morph-target');
      const transition = document.startViewTransition(() => {
        sourceRow.remove();
        itemsList.prepend(optimisticLine);
      });
      transition.finished.finally(() => {
        optimisticLine.classList.remove('cd-morph-target');
      });
    } else if (sourceRow && optimisticLine && itemsList) {
      // Fallback: plain optimistic insert.
      if (!isPinned) sourceRow.remove();
      itemsList.prepend(optimisticLine);
      if (scrolled && scrollHost) scrollHost.scrollTop = 0;
    }

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((parsed) => {
        if (parsed && parsed.status) {
          console.error('[cart-drawer-v4] add failed', parsed);
          // Roll back: remove optimistic line, restore upsell row.
          if (rollback.optimisticLine && rollback.optimisticLine.parentNode) {
            rollback.optimisticLine.remove();
          }
          if (rollback.row && rollback.rowParent) {
            rollback.row.classList.remove('cd-morph-source');
            rollback.rowParent.insertBefore(rollback.row, rollback.rowNextSibling);
          }
          // Server rejected — sync the store back to authoritative
          // state so our optimistic addLine doesn't linger.
          if (window.cartState) window.cartState.refreshFromServer();
          btn.disabled = false;
          return;
        }
        const drawerEl = document.querySelector('cart-drawer');
        // Update the local cart store from the server response BEFORE
        // applying server HTML. The store becomes authoritative as
        // soon as the network returns; re-rendered DOM is just a
        // consequence. Pull cart JSON from the bootstrap blob inside
        // the rendered cart-drawer section (cheap, no extra network).
        const sectionHtml = parsed.sections && parsed.sections['cart-drawer'];
        if (sectionHtml && window.cartState) {
          updateCartStateFromSection(sectionHtml);
        }
        // Re-enable explicitly rather than relying on the card being
        // replaced. A removed card takes its disabled button with it, but
        // the pinned card stays in the DOM and must remain clickable — and
        // the renderContents() call below is guarded three ways, so it
        // cannot be treated as a guaranteed reset.
        btn.disabled = false;
        // Wait for the source card's leave animation to finish before
        // applying the server HTML — otherwise the wholesale DOM swap
        // erases the leaving card mid-transition and the user sees an
        // instant pop. leavePromise resolves immediately for non-card
        // paths, so the list/VT path is unaffected.
        return leavePromise.then(() => {
          if (drawerEl && typeof drawerEl.renderContents === 'function' && parsed.sections) {
            drawerEl.renderContents(parsed);
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-suggestion',
              productVariantId: variantId,
            });
          }
        });
      })
      .catch((err) => {
        console.error('[cart-drawer-v4] add failed', err);
        if (rollback.optimisticLine && rollback.optimisticLine.parentNode) {
          rollback.optimisticLine.remove();
        }
        if (rollback.row && rollback.rowParent) {
          rollback.row.classList.remove('cd-morph-source');
          rollback.rowParent.insertBefore(rollback.row, rollback.rowNextSibling);
        }
        btn.disabled = false;
      });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }

  function escapeAttr(s) {
    return String(s).replace(/"/g, '&quot;');
  }

  /* ───────── Dynamic refresh (called after cart updates) ───────── */

  function refreshDynamic(drawer) {
    populateShareUrl(drawer);
    const textarea = drawer.querySelector('#CartDrawer-Note');
    if (textarea) {
      updateNoteCounter(drawer, textarea);
      updateNoteIndicator(drawer);
    }
    // Returned so the hydrate-hook can await async work (recs fetch).
    return loadSuggestions(drawer);
  }

  /* ───────── Utilities ───────── */

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), wait);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
